import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";

/**
 * Focus Mode state shared by the header, Inbox, Dashboard and Settings.
 * Discern cannot stop Gmail from receiving mail; while focus is active it holds newly
 * arrived mail out of the Inbox, pauses sync, and lets the founder peek or end early.
 */
export const FOCUS_QUICK_MINUTES = [60, 90, 120, 180];

const FocusContext = createContext(null);

const FALLBACK = {
  status: null, active: false, peek: false, setPeek: () => {}, busy: false,
  refresh: async () => null, start: async () => false, end: async () => false,
  toggleVip: async () => null, isVip: () => false, toggleKeyword: async () => null, toggleKeywordMute: async () => null,
  dismissRecap: async () => {}, triageRecap: async () => null,
};

export const fmtMinutes = (minutes) => {
  const m = Math.max(0, Math.round(minutes || 0));
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h && rest) return `${h}h ${rest}m`;
  if (h) return `${h}h`;
  return `${rest}m`;
};

export const fmtLocalTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

export const fmtLocalDay = (iso) => {
  try {
    const d = new Date(iso);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    if (d.toDateString() === today.toDateString()) return "today";
    if (d.toDateString() === tomorrow.toDateString()) return "tomorrow";
    return d.toLocaleDateString([], { weekday: "short" });
  } catch {
    return "";
  }
};

export const FocusProvider = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [peek, setPeek] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/focus/status");
      setStatus(res.data);
      return res.data;
    } catch (error) {
      console.error("Focus status failed:", error);
      return null;
    }
  }, []);

  // Poll once a minute so scheduled windows switch on and off without a reload
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  // Refresh the moment the current session or window is due to end
  useEffect(() => {
    if (!status?.active || !status.until) return undefined;
    const ms = new Date(status.until).getTime() - Date.now();
    if (ms <= 0) return undefined;
    const t = setTimeout(refresh, Math.min(ms + 1000, 2147000000));
    return () => clearTimeout(t);
  }, [status, refresh]);

  const active = !!status?.active;
  useEffect(() => {
    if (!active) setPeek(false);
  }, [active]);

  const start = useCallback(async (minutes) => {
    setBusy(true);
    try {
      const res = await api.post("/focus/start", { minutes });
      setStatus(res.data);
      setPeek(false);
      toast.success(`Focus on until ${fmtLocalTime(res.data.until)}`, {
        description: "New mail is held out of your inbox and sync is paused until then.",
      });
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const end = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.post("/focus/end");
      setStatus(res.data);
      setPeek(false);
      const recap = res.data.recap;
      const through = recap ? (recap.through_total ?? recap.vip_total ?? 0) : 0;
      const arrived = recap ? (recap.held_total || 0) + through : 0;
      toast.success("Focus ended", {
        description: arrived > 0
          ? `${arrived} email${arrived === 1 ? "" : "s"} came in while you focused. Your recap is at the top of the inbox.`
          : "Your inbox is live again. Nothing new came in while you focused.",
      });
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  /** Add or remove a VIP (email or domain). VIP mail is never held during focus. */
  const toggleVip = useCallback(async (value, action = "add") => {
    try {
      const res = await api.post("/focus/vips", { value, action });
      setStatus((s) => (s ? { ...s, vips: res.data.vips } : s));
      toast.success(res.data.is_vip ? `${res.data.value} is a VIP now` : `${res.data.value} removed from VIPs`, {
        description: res.data.is_vip ? "Their mail will always reach you, even during focus." : undefined,
      });
      return res.data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  }, []);

  const isVip = useCallback(
    (fromEmail) => {
      const fe = (fromEmail || "").trim().toLowerCase();
      return (status?.vips || []).some((v) => (v.includes("@") ? fe === v : fe.endsWith(`@${v}`)));
    },
    [status?.vips]
  );

  /** Manage a subject-line keyword. action: add | remove | body_on | body_off. */
  const toggleKeyword = useCallback(async (value, action = "add") => {
    try {
      const res = await api.post("/focus/keywords", { value, action });
      setStatus((s) => (s ? { ...s, keywords: res.data.keywords, keyword_body: res.data.keyword_body } : s));
      const msg =
        action === "remove" ? `"${res.data.value}" removed`
          : action === "body_on" ? `"${res.data.value}" now scans the body too`
          : action === "body_off" ? `"${res.data.value}" scans the subject only`
          : `"${res.data.value}" comes through now`;
      toast.success(msg, {
        description: action === "add" ? "Mail with this word in the subject reaches you during focus." : undefined,
      });
      return res.data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  }, []);

  /** Mute a sender (email or domain) from keyword passthrough so their mail stays held even if it matches. */
  const toggleKeywordMute = useCallback(async (value, action = "add") => {
    try {
      const res = await api.post("/focus/keyword-mutes", { value, action });
      setStatus((s) => (s ? { ...s, keyword_mutes: res.data.keyword_mutes } : s));
      toast.success(res.data.muted ? `"${res.data.value}" muted for keywords` : `"${res.data.value}" unmuted`, {
        description: res.data.muted ? "Their mail stays held during focus even if the subject matches a keyword." : undefined,
      });
      return res.data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  }, []);

  const dismissRecap = useCallback(async (recapId) => {
    try {
      await api.post(`/focus/recap/${recapId}/dismiss`);
      setStatus((s) => (s ? { ...s, recap: null } : s));
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  /** One-tap triage for one sender's mail inside a recap: snooze (tomorrow 9am), read, or task marker. */
  const triageRecap = useCallback(async (recapId, fromEmail, action) => {
    try {
      const res = await api.post(`/focus/recap/${recapId}/triage`, { from_email: fromEmail, action });
      setStatus((s) => {
        if (!s?.recap || s.recap.recap_id !== recapId) return s;
        const senders = (s.recap.senders || []).map((x) => (x.from_email === fromEmail ? { ...x, triaged: action } : x));
        return { ...s, recap: { ...s.recap, senders } };
      });
      return res.data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({ status, active, peek, setPeek, busy, refresh, start, end, toggleVip, isVip, toggleKeyword, toggleKeywordMute, dismissRecap, triageRecap }),
    [status, active, peek, busy, refresh, start, end, toggleVip, isVip, toggleKeyword, toggleKeywordMute, dismissRecap, triageRecap]
  );

  return <FocusContext.Provider value={value}>{children}</FocusContext.Provider>;
};

export const useFocus = () => useContext(FocusContext) || FALLBACK;
